/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { usePrevious, useUpdateEffect } from 'react-use'
import { getOrders } from '../../services/api/orders'
import { GetOrders200Response, Order, OrderPhase } from '../../model'
import DefaultLoader from '../Loaders/DefaultLoader'
import OrderItem from '../OrderItem/OrderItem'
import SearchInput from '../SearchInput/SearchInput'
import Pagination from '../Pagination/Pagination'
import { getSearchParamsAsOb } from '../../helpers/utils'

export interface SearchOptions {
    page: number
    pageSize: number
    phase?: OrderPhase | undefined
    searchString?: string | undefined
}

interface EnabledSearchParams {
    s?: string | undefined
    page?: string | undefined
}

const defaultSearchOptions: SearchOptions = {
    page: 1,
    pageSize: 10,
    phase: undefined, // OrderPhase.Active,
    searchString: ''
}

const OrderOverview: React.FunctionComponent = () => {
    const [searchParams, setSearchParams] = useSearchParams()
    const [loading, setLoading] = useState<boolean>(false)

    const params: any = useParams()
    const previousParams = usePrevious(params)

    const [searchOptions, setSearchOptions] = useState<SearchOptions>({
        ...defaultSearchOptions,
        phase: params.phase ? params.phase : defaultSearchOptions.phase
    })

    const [orders, setOrders] = useState<Order[]>([])
    const [total, setTotal] = useState<number>(0)

    const fetchOrders = async () => {
        setLoading(true)

        const params = {
            ...searchOptions
        }

        try {
            const orderList: GetOrders200Response = await getOrders(params)
            setOrders(orderList?.items || [])
            setTotal(orderList?.total || 0)
            setLoading(false)
        } catch (e) {
            setOrders([])
            setTotal(0)
            console.error(e)
        } finally {
            setLoading(false)
        }
    }

    const updateSearchParams = (params: EnabledSearchParams) => {
        const paramsToSet = Object.fromEntries(Object.entries(params).filter(([_, value]) => value !== undefined))
        setSearchParams(paramsToSet)
    }

    const handleSearchChange = (query: string) => {
        const searchParamsObj = getSearchParamsAsOb(searchParams)
        let params: EnabledSearchParams = { ...searchParamsObj, s: query || undefined }

        // Reset page to first when needed
        if (searchParams.has('page')) {
            params.page = '1'
        }
        updateSearchParams(params)
    }

    const handlePageChange = (page: number) => {
        const searchParamsObj = getSearchParamsAsOb(searchParams)
        const params: EnabledSearchParams = { ...searchParamsObj, page }
        updateSearchParams(params)
    }

    const updateSearchOptions = () => {
        setSearchOptions({
            ...searchOptions,
            searchString: searchParams.get('s') || undefined,
            phase: params.phase ? params.phase : defaultSearchOptions.phase,
            page: searchParams.get('page') ? parseInt(searchParams.get('page') || '1') : 1
        })
    }

    useEffect(() => {
        // Initialize based on search params or just load the first results
        if (searchParams.has('page') || searchParams.has('s')) {
            updateSearchOptions()
        } else {
            fetchOrders()
        }
    }, [])

    useUpdateEffect(() => {
        fetchOrders()
    }, [searchOptions])

    /** Listen to search params change */
    useUpdateEffect(() => {
        updateSearchOptions()
    }, [searchParams])

    /**
     * Clear searchBar when moving to a new phase tab (and set this new phase)
     */
    useUpdateEffect(() => {
        if (previousParams?.phase !== params.phase) {
            setSearchOptions({ ...searchOptions, searchString: undefined, page: 1, phase: params.phase })
        }
    }, [params.phase])

    return (
        <div>
            {/* Searchbar */}
            <div className='flex px-3 lg:px-4 pb-3'>
                <SearchInput
                    initialValue={searchOptions.searchString}
                    placeholder='Zoek op opdracht of locatie'
                    onSearchChange={handleSearchChange}
                    className='w-full max-w-lg'
                />
            </div>
            <div className='flex flex-col'>
                {/* LOADER */}
                {loading ? (
                    <div className='w-full flex justify-center py-4'>
                        <DefaultLoader />
                    </div>
                ) : orders.length === 0 ? (
                    <div className='text-center text-gray-400 py-8'>Geen opdrachten</div>
                ) : (
                    orders.map((order: Order) => {
                        return <OrderItem key={order.id} data={order} actionButtons={['startThread']} showPhase />
                    })
                )}
            </div>
            <Pagination
                page={searchOptions.page}
                itemsPerPage={searchOptions.pageSize}
                totalItems={total}
                onPageChange={handlePageChange}
            />
        </div>
    )
}

export default OrderOverview
